// === ./src/frontend/components/HeroSection.jsx ===
import React, { useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Chip,
  alpha,
} from "@mui/material";
import {
  CloudDownload,
  ArrowForward,
  Shield,
  Security,
  Speed,
  Bolt,
  Storage,
  TrendingUp,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import TorrentMockup from "./TorrentMockup";
import StatsFloatingCard from "./StatsFloatingCard";
import { float, slideInLeft, slideInRight } from "./animations";
import { TranslateContext } from "../../context/TranslateContext";

const HeroSection = ({ theme }) => {
  const history = useHistory();
  const isDark = theme.palette.mode === "dark";
  const { lang } = useContext(TranslateContext);

  return (
    <Box
      id="hero"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        pt: 10,
        background: isDark
          ? `radial-gradient(ellipse at 20% 50%, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 60%),
             radial-gradient(ellipse at 80% 20%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.08)} 0%, transparent 50%),
             ${theme.palette.background.default}`
          : `radial-gradient(ellipse at 20% 50%, ${alpha(theme.palette.primary.main, 0.07)} 0%, transparent 60%),
             radial-gradient(ellipse at 80% 20%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%),
             ${theme.palette.background.default}`,
      }}
    >
      {/* Decorative blobs */}
      {[
        { top: "10%", left: "5%", size: 300, opacity: 0.06, delay: 0 },
        { top: "60%", right: "5%", size: 200, opacity: 0.05, delay: 1 },
        { bottom: "10%", left: "30%", size: 150, opacity: 0.04, delay: 2 },
      ].map((b, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: b.size,
            height: b.size,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, b.opacity)}, transparent)`,
            filter: "blur(40px)",
            top: b.top,
            left: b.left,
            right: b.right,
            bottom: b.bottom,
            animation: `${float} ${5 + b.delay}s ease-in-out infinite`,
            animationDelay: `${b.delay}s`,
            pointerEvents: "none",
          }}
        />
      ))}

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          {/* Left */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ animation: `${slideInLeft} 0.7s ease both` }}>
              <Chip
                icon={<Bolt sx={{ fontSize: 14 }} />}
                label={lang?.heroChip || "Cloud-Powered Torrent Downloading"}
                size="small"
                sx={{
                  mb: 2.5,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                  color: "primary.main",
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.4rem", sm: "3rem", md: "3.6rem" },
                  fontWeight: 800,
                  lineHeight: 1.15,
                  letterSpacing: "-0.03em",
                  mb: 2.5,
                }}
              >
                {lang?.heroTitleStart || "Download Any"}{" "}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || alpha(theme.palette.primary.main, 0.6)})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {lang?.heroTitleHighlight || "Torrent"}
                </Box>{" "}
                {lang?.heroTitleEnd || "to the Cloud"}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  fontSize: "1.05rem",
                  lineHeight: 1.7,
                  mb: 4,
                  maxWidth: 480,
                }}
              >
                {lang?.heroSubtitle ||
                  "Add any magnet link or search for files — our servers handle the peer-to-peer transfer. Your files land safely in your personal cloud storage, accessible from any device."}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={4}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => history.push("/user/register")}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    fontSize: "1rem",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.5)}`,
                    },
                    transition: "all 0.25s ease",
                  }}
                >
                  {lang?.heroCtaPrimary || "Start for Free"}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() =>
                    document
                      .getElementById("how-it-works")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2.5,
                    fontWeight: 600,
                    fontSize: "1rem",
                    borderWidth: "1.5px",
                  }}
                >
                  {lang?.heroCtaSecondary || "See How It Works"}
                </Button>
              </Stack>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                {[
                  {
                    icon: <Shield sx={{ fontSize: 14 }} />,
                    text: lang?.heroBadge1 || "No ISP Throttling",
                  },
                  {
                    icon: <Security sx={{ fontSize: 14 }} />,
                    text: lang?.heroBadge2 || "SSL Encrypted",
                  },
                  {
                    icon: <Speed sx={{ fontSize: 14 }} />,
                    text: lang?.heroBadge3 || "Up to 10 GB/s Servers",
                  },
                ].map((b) => (
                  <Stack
                    key={b.text}
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                  >
                    <Box sx={{ color: "success.main" }}>{b.icon}</Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ fontSize: "0.75rem" }}
                    >
                      {b.text}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* Right */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                animation: `${slideInRight} 0.7s ease both`,
                animationDelay: "0.15s",
              }}
            >
              <TorrentMockup />
              <Box
                sx={{
                  position: "absolute",
                  top: -10,
                  left: { xs: -10, md: -30 },
                }}
              >
                <StatsFloatingCard
                  icon={TrendingUp}
                  label={lang?.heroStatActiveDownloads || "Active Downloads"}
                  value="12,847"
                  color={theme.palette.primary.main}
                  delay={0}
                />
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 20,
                  right: { xs: -10, md: -30 },
                }}
              >
                <StatsFloatingCard
                  icon={Storage}
                  label={lang?.heroStatCloudStorage || "Cloud Storage Used"}
                  value="4.2 TB"
                  color={theme.palette.success.main}
                  delay={0.8}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection;
